import { computed, ComputedRef } from "vue";

import { useCmsStore } from "@/stores/cms.store";
import { useOrganizationStore } from "@/stores/organization.store";
import { useUsersStore } from "@/stores/users.store";

import { Cms } from "@/models/api/cms.model";

export const useCmsAccess = (): {
  visibleCms: ComputedRef<Cms[]>;
  hasConfiguredCms: ComputedRef<boolean>;
  isCmsDataLoading: ComputedRef<boolean>;
} => {
  const cmsStore = useCmsStore();
  const orgStore = useOrganizationStore();
  const usersStore = useUsersStore();

  // Whether the org's CMS list is still being fetched (or hasn't been
  // fetched yet). Callers must check this before treating an empty
  // `hasConfiguredCms`/`visibleCms` as "no CMS" - otherwise a still-loading
  // (or failed) fetch looks identical to a genuinely CMS-less organisation.
  const isCmsDataLoading = computed<boolean>(() => cmsStore.areCmsLoading);

  // Whether the organisation has any CMS configured at all, regardless of
  // whether the current user has access to any of them.
  const hasConfiguredCms = computed<boolean>(() => cmsStore.cms.length > 0);

  const visibleCms = computed<Cms[]>(() => {
    if (!orgStore.canOrgManageCms) {
      return cmsStore.cms;
    }
    const currentUser = usersStore.currentUser;
    if (!currentUser) {
      return [];
    }
    return cmsStore.cms.filter((item: Cms) => {
      return (currentUser.cms ?? []).some(uc => uc.id === item.id);
    });
  });

  return { visibleCms, hasConfiguredCms, isCmsDataLoading };
};
